/* eslint-disable import/no-unresolved */
import React, { Fragment } from 'react';
import { Loading } from '@/components';
import { Card, Container } from 'reactstrap';
import './style.scss';
import { Link, useParams, useHistory } from 'react-router-dom';
import { ArrowLeft } from 'react-feather';
import { colors } from '@/styles/colors';
import EventClose from '@/model/EventClose';
import { EventCloseIncomeSaleBrand } from '@/model/EventCloseIncomeSaleBrand';
import dayjs from 'dayjs';
import { ReactComponent as LocationPin } from '@/assets/images/svg/locationPinNoFill.svg';
import { CustomTable } from '@/components/Table';
import { toCurrency } from '@/helpers/masks/toCurrency';
import SuperButton from '@/components/sharedComponents/SuperButton';
import { toPercentage } from '@/helpers/common/amount';

interface ReportTitle {
  title: string;
}

// eslint-disable-next-line no-shadow
export enum States {
  default = 'default',
  loading = 'loading',
}

interface HomeContainerProps {
  state: States;
  eventState?: EventClose;
  closeEvent: () => void;
}

const SaleBrands: React.FC<{ data: { brands?: EventCloseIncomeSaleBrand[] } }> = ({ data }) => {
  if (!data.brands || data.brands.length === 0) {
    return null;
  }

  return (
    <div className="event-close-sale-brands">
      {data.brands.map((brand, index) => (
        <div className="event-close-sale-brand-row" key={`${brand.brand}-${index}`}>
          <span className="event-close-sale-brand-name">{brand.brand}</span>
          <span>{toCurrency(brand.value)}</span>
          <span>{`${toPercentage(brand.fee)} %`}</span>
          <span>{toCurrency(brand.valueFee)}</span>
        </div>
      ))}
    </div>
  );
};

export const HomeContainer: React.FC<HomeContainerProps> = ({ state, eventState, closeEvent }) => {
  const history = useHistory();
  const { id: eventId } = useParams<{ id: string }>();

  const mountContractorText = (): string => {
    let s = 'Contratantes: ';
    if (
      eventState &&
      eventState.header &&
      eventState.header.contractors &&
      eventState.header.contractors.length > 0
    ) {
      eventState.header.contractors.forEach((data, index) => {
        if (index > 0) {
          s += ' / ';
        }
        s += data;
      });
    }
    return s;
  };

  const sumAmountGeneral = (): number => {
    let amount = 0;
    if (eventState && eventState.generals && eventState.generals.length > 0) {
      eventState.generals.forEach(data => {
        amount += data.value;
      });
    }
    return amount;
  };

  const ReportCards = (): JSX.Element => {
    const newReports: ReportTitle[] = [{ title: 'Receitas' }, { title: 'Despesas' }];

    return (
      <React.Fragment>
        {newReports.map((report, index) => (
          <Card
            key={`Card-Report-${index}`}
            className="report-menu-card card-no-border"
            style={{
              backgroundColor: '#FFFFFF',
              color: '#000000',

              whiteSpace: 'normal',
              textAlign: 'center',
              maxWidth: '180px',
              padding: 'initial',
              minHeight: '100px',
              justifyContent: 'center',
            }}
            onClick={() => {
              if (report.title === 'Receitas') {
                history.push(`/financial/event-close/income/${eventId}`);
              } else if (report.title === 'Despesas') {
                history.push(`/financial/event-close/expense/${eventId}`);
              }
            }}
          >
            {report.title}
          </Card>
        ))}
      </React.Fragment>
    );
  };

  const sumTotalCollected = (): number => {
    let n = 0;
    if (eventState && eventState.generalCollections && eventState.generalCollections.length > 0) {
      eventState.generalCollections.forEach(data => {
        n += data.total;
      });
    }
    return n;
  };

  return (
    <Fragment>
      <Loading isVisible={state === States.loading} />
      <Container className="mainContainer" fluid={true}>
        {eventState ? (
          <>
            <div className="d-flex justify-content-between">
              <div className="pageTitle d-flex" style={{ margin: 0 }}>
                <Link to={`${process.env.PUBLIC_URL}/dashboard/event`}>
                  <ArrowLeft color={colors.black} className="arrow-left" />
                </Link>
                <h5 className="ml-3 mb-0 mt-2 pageTitle">
                  {eventState.header.name} -{' '}
                  {eventState.header.startDate
                    ? dayjs(eventState.header.startDate).format('DD/MM/YYYY')
                    : 'Sem data'}
                </h5>
              </div>
            </div>
            <div className="d-flex">
              <LocationPin style={{ transform: 'scale(0.9)' }} color={colors.black} />
              <div
                className="text-darkgray-regular"
                style={{ marginLeft: '15px', marginTop: '-3px' }}
              >
                {eventState.header.city}
              </div>
            </div>

            <div className="d-flex">
              <div className="text-darkgray-regular">{mountContractorText()}</div>
            </div>
          </>
        ) : (
          <div className="pageTitle d-flex mb-5">
            <Link to={`${process.env.PUBLIC_URL}/dashboard/event`}>
              <ArrowLeft color={colors.black} className="arrow-left" />
            </Link>
            <h5 className="ml-3 mb-0 mt-2 pageTitle">Fechamento de evento</h5>
          </div>
        )}

        <div>
          <h6 className="mb-4 collapseTableText">Arrecadação geral do evento</h6>
          <Card
            className="mb-5 card-no-border customTableScroll"
            style={{ border: 'none', overflow: 'auto' }}
          >
            <CustomTable
              theme="secondary"
              columns={[
                {
                  name: 'Data',
                  selector: row => row.date,
                },
                {
                  name: 'Evento',
                  selector: row => row.name,
                },
                {
                  name: 'Quantidade',
                  selector: row => row.tickets,
                },
                {
                  name: 'Valor Unitário',
                  selector: row => row.saleValue,
                },
                {
                  name: 'Taxa Conveniencia',
                  selector: row => row.fee,
                },
                {
                  name: 'Comissão Garçom',
                  selector: row => row.commission,
                },
                {
                  name: 'Taxa Pagamento',
                  selector: row => row.paymentFee,
                },
                {
                  name: 'Valor Total',
                  selector: row => row.total,
                },
              ]}
              data={
                eventState &&
                eventState.generalCollections &&
                eventState.generalCollections.length > 0
                  ? eventState.generalCollections.map(item => ({
                      date: dayjs(item.date).format('DD/MM/YYYY') || '-----',
                      name: item.name || '-----',
                      tickets: item.tickets || 0,
                      saleValue: item.saleValue ? toCurrency(item.saleValue) : 0,
                      fee: item.fee ? toCurrency(item.fee) : 0,
                      commission: item.commission ? toCurrency(item.commission) : 0,
                      paymentFee: item.paymentFee ? toCurrency(item.paymentFee) : 0,
                      total: item.fee ? toCurrency(item.total) : 0,
                    }))
                  : []
              }
              numberRowsPerPage={10}
              progressPending={false}
            />
            <hr className="mb-0" style={{ minWidth: '1080px' }} />
            <div
              style={{
                padding: '30px 40px',
                gap: '40px',
              }}
              className="d-flex justify-content-center"
            >
              <>
                <div className="d-flex">
                  <div className="mr-1 text-small-gray-regular">Total arrecadação:</div>
                  <div className="text-small-black-600">{toCurrency(sumTotalCollected())}</div>
                </div>
              </>
            </div>
          </Card>
        </div>
        {eventState && eventState.tickets && eventState.tickets.length > 0 && (
          <Fragment>
            <div>
              <h5 className="mb-4 collapseTableText">Ingressos</h5>
            </div>
            {eventState.tickets.map(ticket => (
              <div key={`ticket-${ticket.gateway}-${ticket.fisicalSale}`}>
                <h6 className="collapseTableText" style={{ paddingTop: 0 }}>{`Receitas com venda ${
                  ticket.fisicalSale ? 'física' : 'online'
                }`}</h6>
                <h6
                  className="collapseTableText"
                  style={{ paddingTop: 0 }}
                >{`Gateway ${ticket.gateway}`}</h6>
                <Card
                  className="mb-5 card-no-border customTableScroll"
                  style={{ border: 'none', overflow: 'auto' }}
                >
                  <CustomTable
                    theme="secondary"
                    columns={[
                      {
                        name: 'Forma de pagamento',
                        selector: row => row.paymentForm,
                      },
                      {
                        name: 'Valor',
                        selector: row => row.value,
                      },
                      {
                        name: 'Taxa Média',
                        selector: row => row.administrativeFees,
                      },
                      {
                        name: 'Custo Operação',
                        selector: row => row.paymentFees,
                      },
                    ]}
                    data={
                      ticket.saleValues && ticket.saleValues.length > 0
                        ? ticket.saleValues.map(item => ({
                            // eslint-disable-next-line no-nested-ternary
                            paymentForm: item.paymentForm
                              ? item.paymentForm === 'Crédito'
                                ? `${item.paymentForm} ${item.installments}x`
                                : item.paymentForm
                              : '-----',
                            value: item.value ? toCurrency(item.value) : 0,
                            administrativeFees: toCurrency(item.administrativeFees),
                            paymentFees: toCurrency(item.paymentFees),
                            brands: item.brands,
                          }))
                        : []
                    }
                    numberRowsPerPage={10}
                    progressPending={false}
                    expandableRows
                    expandableRowsHideExpander
                    expandOnRowClicked
                    expandableRowDisabled={row => !row.brands || row.brands.length === 0}
                    expandableRowsComponent={SaleBrands}
                  />
                  <hr className="mb-0" style={{ minWidth: '1080px' }} />
                  <div
                    style={{
                      padding: '30px 40px',
                      gap: '40px',
                    }}
                    className="d-flex justify-content-center"
                  >
                    <>
                      <div className="d-flex">
                        <div className="mr-1 text-small-gray-regular">Valor Arrecadado:</div>
                        <div className="text-small-black-600">
                          {toCurrency(ticket.summary.totalValue)}
                        </div>
                      </div>
                      <div className="d-flex">
                        <div className="mr-1 text-small-gray-regular">Taxa Média:</div>
                        <div className="text-small-black-600">
                          {toCurrency(ticket.summary.percentFees)}
                        </div>
                      </div>
                      <div className="d-flex">
                        <div className="mr-1 text-small-gray-regular">Custo Taxas:</div>
                        <div className="text-small-black-600">
                          {toCurrency(ticket.summary.valueFees)}
                        </div>
                      </div>
                    </>
                  </div>
                </Card>
              </div>
            ))}
          </Fragment>
        )}

        {eventState && eventState.products && eventState.products.length > 0 && (
          <Fragment>
            <div>
              <h5 className="mb-4 collapseTableText">Bar</h5>
            </div>
            {eventState.products.map(product => (
              <div key={`ticket-${product.gateway}-${product.fisicalSale}`}>
                <h6 className="collapseTableText" style={{ paddingTop: 0 }}>{`Receitas com venda ${
                  product.fisicalSale ? 'física' : 'online'
                }`}</h6>
                <h6
                  className="collapseTableText"
                  style={{ paddingTop: 0 }}
                >{`Gateway ${product.gateway}`}</h6>
                <Card
                  className="mb-5 card-no-border customTableScroll"
                  style={{ border: 'none', overflow: 'auto' }}
                >
                  <CustomTable
                    theme="secondary"
                    columns={[
                      {
                        name: 'Forma de pagamento',
                        selector: row => row.paymentForm,
                      },
                      {
                        name: 'Valor',
                        selector: row => row.value,
                      },
                      {
                        name: 'Taxa Média',
                        selector: row => row.administrativeFees,
                      },
                      {
                        name: 'Custo Operação',
                        selector: row => row.paymentFees,
                      },
                    ]}
                    data={
                      product.saleValues && product.saleValues.length > 0
                        ? product.saleValues.map(item => ({
                            // eslint-disable-next-line no-nested-ternary
                            paymentForm: item.paymentForm
                              ? item.paymentForm === 'Crédito'
                                ? `${item.paymentForm} ${item.installments}x`
                                : item.paymentForm
                              : '-----',
                            value: item.value ? toCurrency(item.value) : 0,
                            administrativeFees: toCurrency(item.administrativeFees),
                            paymentFees: toCurrency(item.paymentFees),
                            brands: item.brands,
                          }))
                        : []
                    }
                    numberRowsPerPage={10}
                    progressPending={false}
                    expandableRows
                    expandableRowsHideExpander
                    expandOnRowClicked
                    expandableRowDisabled={row => !row.brands || row.brands.length === 0}
                    expandableRowsComponent={SaleBrands}
                  />
                  <hr className="mb-0" style={{ minWidth: '1080px' }} />
                  <div
                    style={{
                      padding: '30px 40px',
                      gap: '40px',
                    }}
                    className="d-flex justify-content-center"
                  >
                    <>
                      <div className="d-flex">
                        <div className="mr-1 text-small-gray-regular">Valor Arrecadado:</div>
                        <div className="text-small-black-600">
                          {toCurrency(product.summary.totalValue)}
                        </div>
                      </div>
                      <div className="d-flex">
                        <div className="mr-1 text-small-gray-regular">Taxa Média:</div>
                        <div className="text-small-black-600">
                          {toCurrency(product.summary.percentFees)}
                        </div>
                      </div>
                      <div className="d-flex">
                        <div className="mr-1 text-small-gray-regular">Custo Taxas:</div>
                        <div className="text-small-black-600">
                          {toCurrency(product.summary.valueFees)}
                        </div>
                      </div>
                    </>
                  </div>
                </Card>
              </div>
            ))}
          </Fragment>
        )}

        <div>
          <h6 className="mb-4 collapseTableText">Receitas diversas</h6>
          <Card
            className="mb-5 card-no-border customTableScroll"
            style={{ border: 'none', overflow: 'auto' }}
          >
            <CustomTable
              theme="secondary"
              columns={[
                {
                  name: 'Descrição',
                  selector: row => row.description,
                },
                {
                  name: 'Qtd',
                  selector: row => row.amount,
                },
                {
                  name: 'Valor',
                  selector: row => row.value,
                },
              ]}
              data={
                eventState && eventState.generals && eventState.generals.length > 0
                  ? eventState.generals.map(item => ({
                      description: item.description || '-----',
                      amount: item.amount,
                      value: item.value ? toCurrency(item.value) : 0,
                    }))
                  : []
              }
              numberRowsPerPage={10}
              progressPending={false}
            />
            <hr className="mb-0" style={{ minWidth: '1080px' }} />
            <div
              style={{
                padding: '30px 40px',
                gap: '40px',
              }}
              className="d-flex justify-content-center"
            >
              <>
                <div className="d-flex">
                  <div className="mr-1 text-small-gray-regular">Total de receitas:</div>
                  <div className="text-small-black-600">{toCurrency(sumAmountGeneral())}</div>
                </div>
              </>
            </div>
          </Card>
        </div>
        {eventState &&
          eventState.summary &&
          eventState.summary.expense &&
          eventState.summary.expense.length > 0 && (
            <div>
              <h6 className="mb-4 collapseTableText">Despesas</h6>
              <Card
                className="mb-5 card-no-border customTableScroll"
                style={{ border: 'none', overflow: 'auto' }}
              >
                <CustomTable
                  theme="secondary"
                  columns={[
                    {
                      name: 'Descrição',
                      selector: row => row.description,
                    },
                    {
                      name: 'Qtd',
                      selector: row => row.amount,
                    },
                    {
                      name: 'Valor',
                      selector: row => row.value,
                    },
                  ]}
                  data={eventState.summary.expense.map(item => ({
                    description: item.description || '-----',
                    amount: item.amount,
                    value: item.value ? toCurrency(item.value) : 0,
                  }))}
                  numberRowsPerPage={10}
                  progressPending={false}
                />
                <hr className="mb-0" style={{ minWidth: '1080px' }} />
                <div
                  style={{
                    padding: '30px 40px',
                    gap: '40px',
                  }}
                  className="d-flex justify-content-center"
                >
                  <>
                    <div className="d-flex">
                      <div className="mr-1 text-small-gray-regular">Total de receitas:</div>
                      <div className="text-small-black-600">{toCurrency(sumAmountGeneral())}</div>
                    </div>
                  </>
                </div>
              </Card>
            </div>
          )}

        <div>
          <h6 className="mb-4 collapseTableText">Fechamento Final</h6>
          <Card
            className="mb-5 card-no-border customTableScroll"
            style={{ border: 'none', overflow: 'auto' }}
          >
            <h6 className="mb-4 collapseTableText">Receitas</h6>
            <CustomTable
              theme="secondary"
              columns={[
                {
                  name: 'Descrição',
                  selector: row => row.description,
                },
                {
                  name: 'Quantidade',
                  selector: row => row.amount,
                },
                {
                  name: 'Base de Cálculo',
                  selector: row => row.fee,
                },
                {
                  name: 'Valor',
                  selector: row => row.value,
                },
              ]}
              data={
                eventState && eventState.summary && eventState.summary.incomes
                  ? eventState.summary.incomes.map(item => ({
                      description: item.description || '-----',
                      amount: item.amount ? toCurrency(item.amount) : '',
                      fee: `${toPercentage(item.fee)} %`,
                      value: toCurrency(item.value),
                    }))
                  : []
              }
              numberRowsPerPage={10}
              progressPending={false}
            />
            <h6 className="mb-4 collapseTableText">Despesas</h6>
            <CustomTable
              theme="secondary"
              columns={[
                {
                  name: 'Descrição',
                  selector: row => row.description,
                },
                {
                  name: 'Valor',
                  selector: row => row.value,
                },
              ]}
              data={[
                {
                  description: 'Despesas  gerais',
                  value: toCurrency(
                    eventState && eventState.summary && eventState.summary.totalExpense
                      ? eventState.summary.totalExpense
                      : 0,
                  ),
                },
              ]}
              numberRowsPerPage={10}
              progressPending={false}
            />
            <hr className="mb-0" style={{ minWidth: '1080px' }} />
            <div
              style={{
                padding: '30px 40px',
                gap: '40px',
              }}
              className="d-flex justify-content-center"
            >
              <>
                <div className="d-flex">
                  <div className="mr-1 text-small-gray-regular">Valor líquido evento:</div>
                  <div className="text-small-black-600">
                    {toCurrency(
                      eventState && eventState.summary
                        ? eventState.summary.totalIncome - eventState.summary.totalExpense
                        : 0,
                    )}
                  </div>
                </div>
              </>
            </div>
          </Card>
        </div>

        <hr className="mt-5 mb-4" />
        <div className="mb-5 mt-5 report-menu-container">
          <ReportCards />
        </div>
        <div className="nextPageButton">
          <SuperButton style={{ width: '278px' }} onClick={closeEvent}>
            Fechar evento
          </SuperButton>
        </div>
      </Container>
    </Fragment>
  );
};
